import React, { createContext, useContext, useState, useEffect } from "react";
import {
  Usuario,
  CategoriaProfissional,
  RegistroProfissional,
} from "../../../types";
import { db } from "../../../lib/firebase";
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  serverTimestamp,
  onSnapshot,
} from "firebase/firestore";

interface UsuariosContextData {
  usuarios: Usuario[];
  loading: boolean;
  senhaTemporaria: string | null; // ⭐ NOVO: senha gerada
  adicionarUsuario: (
    usuario: Omit<Usuario, "id" | "criadoEm">
  ) => Promise<void>;
  editarUsuario: (id: string, usuario: Partial<Usuario>) => Promise<void>;
  excluirUsuario: (id: string) => Promise<void>;
  bloquearUsuario: (id: string, bloquear: boolean) => Promise<void>;
  buscarUsuarioPorNome: (nome: string) => Usuario[];
  carregarUsuarios: () => Promise<void>;
  limparSenhaTemporaria: () => void; // ⭐ NOVO: limpar senha após copiar
}

const UsuariosContext = createContext<UsuariosContextData>(
  {} as UsuariosContextData
);

// ============================================
// 🔐 GERADOR DE SENHA TEMPORÁRIA
// ============================================
function gerarSenhaTemporaria(): string {
  const maiusculas = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const minusculas = "abcdefghijklmnopqrstuvwxyz";
  const numeros = "0123456789";
  const especiais = "@#$%";

  let senha = "";

  // Garantir pelo menos 1 de cada tipo
  senha += maiusculas[Math.floor(Math.random() * maiusculas.length)];
  senha += minusculas[Math.floor(Math.random() * minusculas.length)];
  senha += numeros[Math.floor(Math.random() * numeros.length)];
  senha += especiais[Math.floor(Math.random() * especiais.length)];

  // Completar com caracteres aleatórios até 14 caracteres (≥12, política B102)
  const todos = maiusculas + minusculas + numeros + especiais;
  for (let i = senha.length; i < 14; i++) {
    senha += todos[Math.floor(Math.random() * todos.length)];
  }

  // Embaralhar
  return senha
    .split("")
    .sort(() => Math.random() - 0.5)
    .join("");
}

// ============================================
// 🔥 FIREBASE REST API - CRIAR USUÁRIO SEM LOGIN
// ============================================
async function criarUsuarioFirebaseAuth(
  email: string,
  senha: string
): Promise<string> {
  const FIREBASE_API_KEY = "AIzaSyDRaC_HDvJPwL6BBFA7-q8wG8YOXdCC6Dw";
  const url = `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${FIREBASE_API_KEY}`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email: email,
        password: senha,
        returnSecureToken: false, // ⭐ NÃO retornar token = NÃO fazer login
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      // Tratar erros
      if (data.error) {
        if (data.error.message === "EMAIL_EXISTS") {
          throw new Error("Este email já está em uso");
        } else if (data.error.message === "INVALID_EMAIL") {
          throw new Error("Email inválido");
        } else if (data.error.message === "WEAK_PASSWORD") {
          throw new Error("Senha muito fraca (mínimo 12 caracteres)");
        }
        throw new Error(data.error.message);
      }
      throw new Error("Erro ao criar usuário");
    }

    console.log("✅ Conta criada via REST API:", data.localId);
    return data.localId; // UID do usuário criado
  } catch (error: any) {
    console.error("❌ Erro na API REST:", error);
    throw error;
  }
}

export function UsuariosProvider({ children }: { children: React.ReactNode }) {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [senhaTemporaria, setSenhaTemporaria] = useState<string | null>(null); // ⭐ NOVO

  // ============================================
  // 📥 CARREGAR USUÁRIOS DO FIREBASE
  // ============================================
  const carregarUsuarios = async () => {
    try {
      setLoading(true);
      console.log("🔍 Carregando usuários do Firebase...");

      const querySnapshot = await getDocs(collection(db, "usuarios"));

      const usuariosFormatados: Usuario[] = querySnapshot.docs.map(
        (docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            nome: data.nome,
            email: data.email,
            tipo: data.tipo,

            // ⭐ NOVOS CAMPOS - CATEGORIA PROFISSIONAL
            categoria: data.categoria || "medico", // Default para compatibilidade
            registroProfissional: data.registroProfissional || undefined,

            // CAMPOS LEGADOS
            crm: data.crm,
            especialidade: data.especialidade,

            // 🏥 DADOS DO SOLICITANTE (B211 — formulários auxiliares SUS)
            dadosSolicitante: data.dadosSolicitante || undefined,

            ativo: data.ativo ?? true,
            criadoEm:
              data.criadoEm?.toDate?.()?.toISOString?.() || data.criadoEm,
            atualizadoEm:
              data.atualizadoEm?.toDate?.()?.toISOString?.() ||
              data.atualizadoEm,
          } as Usuario;
        }
      );

      setUsuarios(usuariosFormatados);
      console.log("✅ Usuários carregados:", usuariosFormatados.length);
    } catch (error: any) {
      console.error("❌ Erro ao carregar usuários:", error);
      // ⭐ NÃO mostrar erro se for problema de autenticação
      if (error.code !== "permission-denied") {
        console.error("Erro inesperado:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // 🔄 ESCUTA EM TEMPO REAL
  // ============================================
  useEffect(() => {
    console.log("🔍 Escutando usuários em tempo real...");

    const unsubscribe = onSnapshot(
      collection(db, "usuarios"),
      (snapshot) => {
        const usuariosFormatados: Usuario[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            nome: data.nome,
            email: data.email,
            tipo: data.tipo,
            categoria: data.categoria || "medico",
            registroProfissional: data.registroProfissional || undefined,
            crm: data.crm,
            especialidade: data.especialidade,
            dadosSolicitante: data.dadosSolicitante || undefined,
            ativo: data.ativo ?? true,
            criadoEm:
              data.criadoEm?.toDate?.()?.toISOString?.() || data.criadoEm,
            atualizadoEm:
              data.atualizadoEm?.toDate?.()?.toISOString?.() ||
              data.atualizadoEm,
          } as Usuario;
        });

        setUsuarios(usuariosFormatados);
        setLoading(false);
        console.log(
          "✅ Usuários atualizados (tempo real):",
          usuariosFormatados.length
        );
      },
      (error) => {
        console.error("❌ Erro no listener de usuários:", error);
        if (error.code !== "permission-denied") {
          console.error("Erro inesperado:", error);
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);
  // ============================================
  // ➕ ADICIONAR USUÁRIO (COM REST API)
  // ============================================
  const adicionarUsuario = async (
    usuario: Omit<Usuario, "id" | "criadoEm">
  ) => {
    try {
      // B318-FU (AI_143, M5): categoria basta p/ depurar; registro (CRM/COREN)
      // é dado pessoal — fora do console.
      console.log("📝 Adicionando usuário...");
      console.log("📋 Categoria:", usuario.categoria);

      // ========================================
      // 1️⃣ GERAR SENHA TEMPORÁRIA
      // ========================================
      const senha = gerarSenhaTemporaria();
      // B318-FU (AI_143): NUNCA logar a senha (credencial em texto claro). Ela
      // é entregue ao admin pela UI (setSenhaTemporaria) para repasse seguro.
      setSenhaTemporaria(senha); // ⭐ SALVAR NO ESTADO

      // ========================================
      // 2️⃣ CRIAR CONTA NO FIREBASE AUTH VIA REST API
      // ========================================
      console.log("🔥 Criando usuário via REST API...");
      const uid = await criarUsuarioFirebaseAuth(usuario.email, senha);
      console.log("✅ UID do novo usuário:", uid);

      // ========================================
      // 3️⃣ PREPARAR DADOS PARA O FIRESTORE
      // ========================================
      const dadosFirebase: any = {
        nome: usuario.nome,
        email: usuario.email,
        tipo: usuario.tipo,

        // ⭐ NOVOS CAMPOS - CATEGORIA PROFISSIONAL
        categoria: usuario.categoria,
        registroProfissional: usuario.registroProfissional || null,

        // CAMPOS LEGADOS
        crm: usuario.crm || null,
        especialidade: usuario.especialidade || null,

        // 🏥 DADOS DO SOLICITANTE (B211 — formulários auxiliares SUS)
        dadosSolicitante: usuario.dadosSolicitante || null,

        ativo: usuario.ativo ?? true,
        // 🔒 B103 — usuário criado com senha temporária deve trocá-la no 1º acesso
        precisaTrocarSenha: true,
        criadoEm: serverTimestamp(),
        atualizadoEm: serverTimestamp(),
      };

      // ========================================
      // 4️⃣ CRIAR DOCUMENTO NO FIRESTORE
      // ========================================
      console.log("💾 Salvando no Firestore...");
      await setDoc(doc(db, "usuarios", uid), dadosFirebase);
      console.log("✅ Dados salvos no Firestore!");

      console.log("✅ Usuário adicionado com sucesso!");
    } catch (error: any) {
      console.error("❌ Erro ao adicionar usuário:", error);
      setSenhaTemporaria(null); // Limpar senha em caso de erro
      throw error;
    }
  };

  // ============================================
  // 🧹 LIMPAR SENHA TEMPORÁRIA
  // ============================================
  const limparSenhaTemporaria = () => {
    setSenhaTemporaria(null);
  };

  // ============================================
  // ✏️ EDITAR USUÁRIO
  // ============================================
  const editarUsuario = async (
    id: string,
    dadosAtualizados: Partial<Usuario>
  ) => {
    try {
      // B318-FU (AI_143, M5): sem dump do payload (nome/e-mail/registro) —
      // só o id e as chaves alteradas (não os valores).
      console.log(
        "📝 Editando usuário:",
        id,
        "campos:",
        Object.keys(dadosAtualizados)
      );

      const dadosFirebase: any = {
        atualizadoEm: serverTimestamp(),
      };

      // Adiciona apenas campos que foram alterados
      Object.keys(dadosAtualizados).forEach((key) => {
        const value = (dadosAtualizados as any)[key];
        if (value !== undefined && key !== "id" && key !== "criadoEm") {
          dadosFirebase[key] = value;
        }
      });

      await updateDoc(doc(db, "usuarios", id), dadosFirebase);

      console.log("✅ Usuário editado com sucesso!");
    } catch (error) {
      console.error("❌ Erro ao editar usuário:", error);
      throw error;
    }
  };

  // ============================================
  // 🗑️ EXCLUIR USUÁRIO
  // ============================================
  const excluirUsuario = async (id: string) => {
    // Não permitir excluir se for o único administrador
    const admins = usuarios.filter(
      (u) => u.tipo === "administrador" && u.ativo
    );
    const usuarioAExcluir = usuarios.find((u) => u.id === id);

    if (usuarioAExcluir?.tipo === "administrador" && admins.length === 1) {
      alert("❌ Não é possível excluir o único administrador do sistema!");
      return;
    }

    if (!window.confirm("⚠️ Tem certeza que deseja excluir este usuário?")) {
      return;
    }

    try {
      console.log("🗑️ Excluindo usuário:", id);

      await deleteDoc(doc(db, "usuarios", id));

      console.log("✅ Usuário excluído com sucesso!");
    } catch (error) {
      console.error("❌ Erro ao excluir usuário:", error);
      throw error;
    }
  };

  // ============================================
  // 🔒 BLOQUEAR/DESBLOQUEAR USUÁRIO
  // ============================================
  const bloquearUsuario = async (id: string, bloquear: boolean) => {
    // Não permitir bloquear se for o único administrador ativo
    const admins = usuarios.filter(
      (u) => u.tipo === "administrador" && u.ativo
    );
    const usuarioABloquear = usuarios.find((u) => u.id === id);

    if (
      usuarioABloquear?.tipo === "administrador" &&
      admins.length === 1 &&
      bloquear
    ) {
      alert(
        "❌ Não é possível bloquear o único administrador ativo do sistema!"
      );
      return;
    }

    try {
      console.log(
        `🔒 ${bloquear ? "Bloqueando" : "Desbloqueando"} usuário:`,
        id
      );

      await editarUsuario(id, { ativo: bloquear });

      console.log(
        `✅ Usuário ${bloquear ? "bloqueado" : "desbloqueado"} com sucesso!`
      );
    } catch (error) {
      console.error("❌ Erro ao bloquear/desbloquear usuário:", error);
      throw error;
    }
  };

  // ============================================
  // 🔍 BUSCAR USUÁRIO POR NOME
  // ============================================
  const buscarUsuarioPorNome = (nome: string): Usuario[] => {
    if (!nome) return usuarios;

    const termoBusca = nome.toLowerCase();
    return usuarios.filter(
      (usuario) =>
        usuario.nome.toLowerCase().includes(termoBusca) ||
        usuario.email.toLowerCase().includes(termoBusca) ||
        // ⭐ BUSCAR TAMBÉM POR REGISTRO PROFISSIONAL
        usuario.registroProfissional?.numero?.toLowerCase().includes(termoBusca)
    );
  };

  return (
    <UsuariosContext.Provider
      value={{
        usuarios,
        loading,
        senhaTemporaria, // ⭐ NOVO
        adicionarUsuario,
        editarUsuario,
        excluirUsuario,
        bloquearUsuario,
        buscarUsuarioPorNome,
        carregarUsuarios,
        limparSenhaTemporaria, // ⭐ NOVO
      }}
    >
      {children}
    </UsuariosContext.Provider>
  );
}

export function useUsuarios() {
  const context = useContext(UsuariosContext);
  if (!context) {
    throw new Error("useUsuarios deve ser usado dentro de UsuariosProvider");
  }
  return context;
}
